import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { toast } from "sonner";
import { CircleCheck, CircleX, HelpCircle } from "lucide-react";
import Dialog from "../../../../../components/shared/Dialog";
import Button from "../../../../../components/shared/Button";
import BaseForm, { Field } from "../../../../../components/shared/Form";
import { Textarea } from "../../../../../components/ui/textarea";
import { useVisible } from "../../../../../utils/hooks/useVisible";
import useApi from "../../../../../utils/api";
import { getErrorMessage } from "../../../../../utils/helpers/errors";
import { withLoadingToast } from "../../helpers/withLoadingToast";

interface UseApproveDialogProps {
    queryOptions: { queryKey: QueryKey };
    onSuccess?: () => void;
}

type ApproveFormValues = {
    approveReason: string;
};

export const useApproveDialog = ({ queryOptions, onSuccess }: UseApproveDialogProps) => {
    const { t } = useTranslation();
    const { authPut } = useApi();
    const queryClient = useQueryClient();
    const { open, visible, close, selected } = useVisible();

    const form = useForm<ApproveFormValues>({
        defaultValues: { approveReason: "" },
    });

    const approveMutate = useMutation({
        ...withLoadingToast(t("approving")),
        mutationFn: async ({ ids, approveReason }: { ids: string[]; approveReason: string }) => {
            return Promise.all(
                ids.map((id) => authPut(`/request/${id}/approve`, approveReason && { data: { approveReason } })),
            );
        },
        onSuccess: (responses) => {
            queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
            onSuccess?.();

            if (Array.isArray(selected?.ids) || Array.isArray(selected)) {
                toast.success(t("requests-are-confirmed"));
                selected?.refetch?.();
            } else {
                toast.success(t("request-approved-successfully"));
                selected?.close?.(responses);
            }

            form.reset();
            close();
        },
        onError: (error) => {
            getErrorMessage(error, t);
            close();
        },
    });

    const onFinish = useCallback((value: ApproveFormValues) => {
        if (Array.isArray(selected?.ids)) {
            approveMutate.mutate({ ids: selected.ids, approveReason: value.approveReason });
        } else if (Array.isArray(selected)) {
            approveMutate.mutate({ ids: selected, approveReason: value.approveReason });
        } else {
            approveMutate.mutate({ ids: [selected?.id], approveReason: value.approveReason });
        }
    }, [selected, approveMutate]);

    const onClose = useCallback(() => {
        form.reset();
        close();
    }, [close, form]);

    const dialog = useMemo(
        () => (
            <Dialog open={visible} close={onClose} size="lg">
                <div className="tw-flex tw-flex-col tw-items-center tw-text-center tw-mb-5">
                    <div className="tw-w-12 tw-h-12 tw-rounded-full tw-bg-primary/10 tw-flex tw-items-center tw-justify-center tw-mb-3">
                        <HelpCircle size={22} className="tw-text-primary" />
                    </div>
                    <h2 className="tw-text-sm tw-font-semibold">{t("do-you-want-to-approve-request")}</h2>
                </div>

                <BaseForm form={form} onFinish={onFinish}>
                    <Field name="approveReason" label={t("reason")}>
                        <Textarea rows={3} className="tw-resize-none field-sizing-fixed" placeholder={t("reason")} />
                    </Field>

                    <div className="tw-flex tw-justify-end tw-gap-2 tw-pt-2">
                        <Button type="button" variant="outline" onClick={onClose} disabled={approveMutate.isPending}>
                            <CircleX />
                            {t("cancel")}
                        </Button>

                        <Button type="submit" loading={approveMutate.isPending} disabled={approveMutate.isPending}>
                            <CircleCheck />
                            {t("approve")}
                        </Button>
                    </div>
                </BaseForm>
            </Dialog>
        ),
        [form, visible, onClose, onFinish, t, approveMutate.isPending],
    );

    return { open, dialog };
};
